/**
 * Système de permissions basé sur les rôles
 */

// Actions possibles
export const ACTIONS = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete'
};

// Resources de l'application
export const RESOURCES = {
  DASHBOARD: 'dashboard',
  TEMPS: 'temps',
  TEMPS_OWN: 'temps_own', // Temps du consultant lui-même
  TMP: 'tmp',
  FACTURATION: 'facturation',
  FACTURATION_STATUS: 'facturation_status', // Changement du statut des factures externes
  CHARGES: 'charges',
  USERS: 'users',
  BANKING: 'banking' // Gestion des connexions bancaires
};

// Matrice de permissions par rôle
const PERMISSIONS_MATRIX = {
  super_admin: {
    [RESOURCES.DASHBOARD]: [ACTIONS.READ, ACTIONS.WRITE],
    [RESOURCES.TEMPS]: [ACTIONS.READ, ACTIONS.WRITE, ACTIONS.DELETE],
    [RESOURCES.TEMPS_OWN]: [ACTIONS.READ, ACTIONS.WRITE, ACTIONS.DELETE],
    [RESOURCES.TMP]: [ACTIONS.READ, ACTIONS.WRITE],
    [RESOURCES.FACTURATION]: [ACTIONS.READ, ACTIONS.WRITE],
    [RESOURCES.FACTURATION_STATUS]: [ACTIONS.WRITE], // Seuls les super admins peuvent changer le statut
    [RESOURCES.CHARGES]: [ACTIONS.READ, ACTIONS.WRITE],
    [RESOURCES.USERS]: [ACTIONS.READ, ACTIONS.WRITE, ACTIONS.DELETE],
    [RESOURCES.BANKING]: [ACTIONS.READ, ACTIONS.WRITE, ACTIONS.DELETE]
  },
  consultant: {
    [RESOURCES.DASHBOARD]: [ACTIONS.READ],
    [RESOURCES.TEMPS]: [ACTIONS.READ], // Peut lire tous les temps
    [RESOURCES.TEMPS_OWN]: [ACTIONS.READ, ACTIONS.WRITE, ACTIONS.DELETE], // Peut éditer les siens
    [RESOURCES.TMP]: [ACTIONS.READ, ACTIONS.WRITE], // Peut lancer le recalcul TMP
    [RESOURCES.FACTURATION]: [ACTIONS.READ, ACTIONS.WRITE],
    [RESOURCES.FACTURATION_STATUS]: [], // Les consultants ne peuvent PAS changer le statut
    [RESOURCES.CHARGES]: [ACTIONS.READ, ACTIONS.WRITE], // Peut saisir des charges
    [RESOURCES.USERS]: [], // Pas d'accès
    [RESOURCES.BANKING]: [ACTIONS.READ] // Lecture seule des connexions bancaires
  },
  visiteur: {
    [RESOURCES.DASHBOARD]: [ACTIONS.READ],
    [RESOURCES.TEMPS]: [ACTIONS.READ],
    [RESOURCES.TEMPS_OWN]: [ACTIONS.READ],
    [RESOURCES.TMP]: [ACTIONS.READ],
    [RESOURCES.FACTURATION]: [ACTIONS.READ],
    [RESOURCES.FACTURATION_STATUS]: [], // Les visiteurs ne peuvent PAS changer le statut
    [RESOURCES.CHARGES]: [ACTIONS.READ],
    [RESOURCES.USERS]: [], // Pas d'accès
    [RESOURCES.BANKING]: [ACTIONS.READ] // Lecture seule des connexions bancaires
  }
};

/**
 * Vérifie si un utilisateur a la permission pour une action sur une ressource
 * @param {string} role - Le rôle de l'utilisateur (super_admin, consultant, visiteur)
 * @param {string} action - L'action à vérifier (read, write, delete)
 * @param {string} resource - La ressource concernée
 * @returns {boolean} - true si l'utilisateur a la permission
 */
export const checkPermission = (role, action, resource) => {
  if (!role || !action || !resource) return false;

  const rolePermissions = PERMISSIONS_MATRIX[role];
  if (!rolePermissions) return false;

  const resourcePermissions = rolePermissions[resource];
  if (!resourcePermissions) return false;

  return resourcePermissions.includes(action);
};

/**
 * Vérifie si un temps appartient à l'utilisateur connecté
 * @param {object} temps - L'enregistrement de temps
 * @param {string} userConsultantId - L'ID du consultant lié à l'utilisateur
 * @returns {boolean} - true si le temps appartient à l'utilisateur
 */
export const isOwnTemps = (temps, userConsultantId) => {
  if (!temps || !userConsultantId) return false;

  // Le consultant_id dans temps peut être un tableau ou une string
  const tempsConsultantId = Array.isArray(temps.fields?.Consultant)
    ? temps.fields.Consultant[0]
    : temps.fields?.Consultant;

  return tempsConsultantId === userConsultantId;
};

/**
 * Vérifie si l'utilisateur peut éditer un temps spécifique
 * @param {string} role - Le rôle de l'utilisateur
 * @param {object} temps - L'enregistrement de temps
 * @param {string} userConsultantId - L'ID du consultant lié à l'utilisateur
 * @returns {boolean} - true si l'utilisateur peut éditer ce temps
 */
export const canEditTemps = (role, temps, userConsultantId) => {
  // Super admin peut tout éditer
  if (role === 'super_admin') {
    return checkPermission(role, ACTIONS.WRITE, RESOURCES.TEMPS);
  }

  // Consultant peut éditer ses propres temps
  if (role === 'consultant') {
    return isOwnTemps(temps, userConsultantId) &&
           checkPermission(role, ACTIONS.WRITE, RESOURCES.TEMPS_OWN);
  }

  // Visiteur ne peut rien éditer
  return false;
};

/**
 * Vérifie si l'utilisateur peut supprimer un temps spécifique
 * @param {string} role - Le rôle de l'utilisateur
 * @param {object} temps - L'enregistrement de temps
 * @param {string} userConsultantId - L'ID du consultant lié à l'utilisateur
 * @returns {boolean} - true si l'utilisateur peut supprimer ce temps
 */
export const canDeleteTemps = (role, temps, userConsultantId) => {
  // Super admin peut tout supprimer
  if (role === 'super_admin') {
    return checkPermission(role, ACTIONS.DELETE, RESOURCES.TEMPS);
  }

  // Consultant peut supprimer ses propres temps
  if (role === 'consultant') {
    return isOwnTemps(temps, userConsultantId) &&
           checkPermission(role, ACTIONS.DELETE, RESOURCES.TEMPS_OWN);
  }

  // Visiteur ne peut rien supprimer
  return false;
};
